import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useParticipantsStore } from '../../stores/participantsStore';
import VideoTile from './VideoTile';
import { useAdaptiveGrid } from '../../hooks/useAdaptiveGrid';

const VideoGrid = () => {
  const { participants, localParticipant } = useParticipantsStore();
  const [expandedParticipantId, setExpandedParticipantId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 800, height: 600 });
  
  // Track if expansion was triggered by screen share (auto) vs manual click
  const isAutoExpandRef = useRef<boolean>(false);
  
  // Speaker prioritization state
  const [displayedParticipantIds, setDisplayedParticipantIds] = useState<string[]>([]);
  const lastReorderTimeRef = useRef<number>(0);
  const REORDER_COOLDOWN_MS = 5000; // 5 seconds between reorderings
  
  // Tick to periodically re-evaluate speaking duration (every 1 second)
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Track container size changes (including when sidebar opens/closes)
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };

    updateSize();
    
    // Use ResizeObserver for container size changes
    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateSize);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  // Build participant map for quick lookup
  const participantMap = useMemo(() => {
    const map = new Map<string, typeof localParticipant>();
    if (localParticipant) {
      map.set(localParticipant.id, localParticipant);
    }
    participants.forEach((p, id) => map.set(id, p));
    return map;
  }, [participants, localParticipant]);

  // Compute current priority speakers (who qualifies for priority NOW)
  const currentPrioritySpeakerIds = useMemo(() => {
    const list = Array.from(participants.values());
    const baseList = localParticipant ? [localParticipant, ...list] : list;
    
    const now = Date.now();
    const SPEAKING_THRESHOLD_MS = 5000; // 5 seconds of speaking to qualify
    
    const prioritySpeakers: { id: string; startTime: number }[] = [];
    
    baseList.forEach(p => {
      const isSpeakingLong = p.isSpeaking && 
                             p.speakingStartTime && 
                             (now - p.speakingStartTime) >= SPEAKING_THRESHOLD_MS;
      
      if (isSpeakingLong) {
        prioritySpeakers.push({ id: p.id, startTime: p.speakingStartTime! });
      }
    });
    
    // Sort by speaking start time (stable priority)
    prioritySpeakers.sort((a, b) => a.startTime - b.startTime);
    
    return prioritySpeakers.map(p => p.id);
  }, [participants, localParticipant, tick]);

  // Track which speakers have been prioritized (to detect NEW speakers)
  const prioritizedSpeakersRef = useRef<Set<string>>(new Set());

  // Effect to update displayed order - only when NEW speaker qualifies
  useEffect(() => {
    const now = Date.now();
    const timeSinceLastReorder = now - lastReorderTimeRef.current;
    
    // Condition 1: Don't reorder if a tile is expanded
    if (expandedParticipantId) {
      return;
    }
    
    // Check for NEW priority speakers (not already prioritized)
    const newPrioritySpeakers = currentPrioritySpeakerIds.filter(
      id => !prioritizedSpeakersRef.current.has(id)
    );
    
    // Only reorder if there are NEW speakers to prioritize
    if (newPrioritySpeakers.length === 0) {
      // Initialize displayed order if empty
      if (displayedParticipantIds.length === 0) {
        const allIds = localParticipant 
          ? [localParticipant.id, ...Array.from(participants.keys())]
          : Array.from(participants.keys());
        setDisplayedParticipantIds(allIds);
      }
      return;
    }
    
    // Condition 2: Respect cooldown (5s since last reorder)
    if (timeSinceLastReorder < REORDER_COOLDOWN_MS && displayedParticipantIds.length > 0) {
      return;
    }
    
    // Build new order: new priority speakers at top, then existing order
    const newOrder: string[] = [];
    const usedIds = new Set<string>();
    
    // Add new priority speakers first (sorted by start time)
    for (const id of newPrioritySpeakers) {
      newOrder.push(id);
      usedIds.add(id);
      prioritizedSpeakersRef.current.add(id); // Mark as prioritized
    }
    
    // Then add existing displayed order (keeping previous priority speakers at top)
    for (const id of displayedParticipantIds) {
      if (!usedIds.has(id)) {
        newOrder.push(id);
        usedIds.add(id);
      }
    }
    
    // Add any participants not in displayed order yet
    const allParticipantIds = localParticipant 
      ? [localParticipant.id, ...Array.from(participants.keys())]
      : Array.from(participants.keys());
    for (const id of allParticipantIds) {
      if (!usedIds.has(id)) {
        newOrder.push(id);
      }
    }
    
    setDisplayedParticipantIds(newOrder);
    lastReorderTimeRef.current = now;
  }, [currentPrioritySpeakerIds, expandedParticipantId, displayedParticipantIds, REORDER_COOLDOWN_MS, participants, localParticipant]);

  // Build final participant list based on displayed order
  const allParticipants = useMemo(() => {
    // Get all participant IDs
    const allIds = localParticipant 
      ? [localParticipant.id, ...Array.from(participants.keys())]
      : Array.from(participants.keys());
    
    // If no displayed order yet, use default order
    if (displayedParticipantIds.length === 0) {
      return allIds.map(id => participantMap.get(id)).filter(Boolean) as NonNullable<typeof localParticipant>[];
    }
    
    // Use displayed order, add any new participants at the end
    const result: NonNullable<typeof localParticipant>[] = [];
    const usedIds = new Set<string>();
    
    for (const id of displayedParticipantIds) {
      const p = participantMap.get(id);
      if (p) {
        result.push(p);
        usedIds.add(id);
      }
    }
    
    // Add new participants not in displayed order
    for (const id of allIds) {
      if (!usedIds.has(id)) {
        const p = participantMap.get(id);
        if (p) result.push(p);
      }
    }
    
    return result;
  }, [displayedParticipantIds, participantMap, participants, localParticipant]);

  const layout = useAdaptiveGrid(allParticipants.length, containerSize);

  const handleToggleExpand = useCallback((participantId: string) => {
    isAutoExpandRef.current = false; // Mark as manual expansion
    setExpandedParticipantId(prev => prev === participantId ? null : participantId);
  }, []);

  // Автораскрытие при демонстрации экрана
  useEffect(() => {
    const screenSharingParticipant = allParticipants.find(p => p.screenShareEnabled);
    if (screenSharingParticipant) {
      isAutoExpandRef.current = true; // Mark as auto expansion
      setExpandedParticipantId(screenSharingParticipant.id);
    } else if (isAutoExpandRef.current) {
      // Only collapse if it was auto-expanded (not manual)
      setExpandedParticipantId(null);
      isAutoExpandRef.current = false;
    }
    // Don't collapse manually expanded tiles
  }, [allParticipants]);

  // Находим развернутого участника
  const expandedParticipant = expandedParticipantId 
    ? allParticipants.find(p => p.id === expandedParticipantId)
    : null;

  if (allParticipants.length === 0) {
    return (
      <div ref={containerRef} className="flex-1 flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="text-gray-400 text-lg mb-2">Нет участников</div>
          <div className="text-gray-500 text-sm">Ожидание подключения...</div>
        </div>
      </div>
    );
  }

  // Режим развернутой плитки
  if (expandedParticipant) {
    return (
      <div ref={containerRef} className="flex-1 flex items-center justify-center bg-gray-900 p-2 sm:p-4 overflow-hidden">
        {/* Видимая развернутая плитка */}
        <div 
          className="relative w-full h-full"
          style={{
            maxWidth: 'min(calc(100vw - 1rem), calc((100vh - 180px) * 16 / 9))',
            maxHeight: 'min(calc(100vh - 180px), calc((100vw - 1rem) * 9 / 16))',
          }}
        >
          <VideoTile
            participant={expandedParticipant}
            isLocal={expandedParticipant.id === localParticipant?.id}
            isExpanded={true}
            onToggleExpand={() => handleToggleExpand(expandedParticipant.id)}
            isCurrentUserHost={localParticipant?.isHost || false}
          />
        </div>
        
        {/* Скрытые плитки остальных участников для воспроизведения звука */}
        <div className="hidden">
          {allParticipants
            .filter(p => p.id !== expandedParticipantId)
            .map((participant) => (
              <VideoTile
                key={participant.id}
                participant={participant}
                isLocal={participant.id === localParticipant?.id}
                isCurrentUserHost={localParticipant?.isHost || false}
              />
            ))
          }
        </div>
      </div>
    );
  }

  // Обычный режим сетки
  const isMobile = containerSize.width < 640;
  const gridGap = isMobile ? 8 : 16;
  const gridPaddingClass = isMobile ? 'p-2' : 'p-4';

  return (
    <div 
      ref={containerRef}
      className={`flex-1 flex bg-gray-900 ${gridPaddingClass} ${layout.shouldScroll ? 'items-start justify-center overflow-y-auto overflow-x-hidden' : 'items-center justify-center overflow-hidden'}`}
    >
      <div 
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${layout.cols}, minmax(0, ${layout.tileWidth}px))`,
          gap: `${gridGap}px`,
          width: '100%',
          maxWidth: `${layout.cols * layout.tileWidth + (layout.cols - 1) * gridGap}px`,
        }}
      >
        {allParticipants.map((participant) => (
          <div
            key={participant.id}
            style={{
              width: `${layout.tileWidth}px`,
              height: `${layout.tileHeight}px`,
            }}
          >
            <VideoTile
              participant={participant}
              isLocal={participant.id === localParticipant?.id}
              isExpanded={false}
              onToggleExpand={() => handleToggleExpand(participant.id)}
              isCurrentUserHost={localParticipant?.isHost || false}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default VideoGrid;
